import {BettingTypesMap} from "features/core/definitions";

// Ekrandaki seçim bağlamının hâlâ oynanabilir olup olmadığını söyleyen SAF katman.
// Bülten 5 saniyede bir tazeleniyor ve kapanma bir olay olarak gelmiyor: kart `status`ünü
// kaybediyor, tür `wager_details`ten düşüyor, koşu türün `runs`undan çıkıyor. Sayfa bunu
// kendi başına fark etmediği sürece kullanıcı kapanmış bir bağlamda oynamaya devam ediyor.
//
// İki tüketici, iki ayrı politika (sepet modelleri farklı olduğu için):
//   * Müşterek  - kupon (kart+tür+alt tür) bağlamına kilitli, taşımak seçimleri GİZLER.
//                 Bu yüzden bağlam kırılınca modal + sıfırlama.
//   * Sabit ihtimalli - sepet bağlam-bağımsız (çok kartlı/çok koşulu), taşımak hiçbir şeyi
//                 gizlemez. Bu yüzden sessiz geçiş; sepetteki geçersiz bacak AYRICA
//                 satırında işaretlenir.

/** Seçim bağlamının kırıldığı ilk kademe. */
export type SelectionBreak = "ok" | "run" | "type" | "card";

/**
 * Kart hâlâ ilgili listede mi. Bülten haritaları kapalı kartları da taşıdığı için
 * "kart çözülüyor mu" yeterli bir ölçüt DEĞİL; kartın oynanabilir listede durması gerekiyor.
 */
export function cardStillListed(id: any, list: any[]): boolean {
    return id != null && list.some((el: any) => el._id === id);
}

/** `wager_details[].runs` her iki biçimde de gelebiliyor (dizi ya da "1,2,3"). */
export function wagerRunNos(wager: any): number[] {
    const raw = Array.isArray(wager?.runs)
        ? wager.runs
        : String(wager?.runs ?? "").split(",").filter((r: string) => r !== "");
    return raw.map((r: any) => Number(r));
}

/** Müşterek bağlamı: kart → tür → alt tür (ayak grubunun başlangıç koşusu). */
export function mutualSelectionBreak(params: {
    card: any;
    availableCards: any[];
    typeId: any;
    subTypeId: any;
}): SelectionBreak {
    const {card, availableCards, typeId, subTypeId} = params;
    if (!card || !cardStillListed(card._id, availableCards)) {
        return "card";
    }
    // Tür/koşu doğrudan `wager_details`ten okunuyor - `buildMutualBettingTypes` ile aynı
    // süzgeç, ama bu modül SAF kalsın diye bülten servisi import edilmiyor.
    const catalogType: any = BettingTypesMap[Number(typeId)];
    const wager = (card.wager_details ?? []).find((w: any) => Number(w.type) === Number(typeId));
    if (!wager || !catalogType || catalogType.isFob || catalogType.available !== true) {
        return "type";
    }
    const runs: any[] = Array.isArray(wager.runs)
        ? wager.runs
        : String(wager.runs ?? "").split(",").filter((r: string) => r !== "");
    if (subTypeId == null || !runs.some((r: any) => Number(r) === Number(subTypeId))) {
        return "run";
    }
    return "ok";
}

// --- sabit ihtimalli ------------------------------------------------------------------

/**
 * Bir oran girdisi seçilebilir mi. Gösterimle AYNI kural.
 * **KO burada GEÇERLİ**: kart açılışında TJK oranı henüz açıklamamıştır, girdi
 * `status:'o'` + `win_odds` 1.00 gelir ve bahis oynanabilir. Kapanmış koşu bu değil,
 * kilittir (`show:false` / `bettable:false`). İkisi ham veride birbirine çok benziyor -
 * ayıran tek alan `status`. Karıştırılırsa kart açılışında (her koşu KO) kullanıcı
 * kartın sonuna süpürülür.
 */
export function isOddsEntrySelectable(entry: any): boolean {
    if (entry?.show === false || entry?.bettable === false) {
        return false;
    }
    return !!((entry?.win_odds && Number(entry.win_odds) > 1) || entry?.status === "o");
}

/** Koşu, kartın tarifesinde bu FOB türü için açılmış mı. */
export function isRunEligibleForFobBettingType(hippodrome: any, run: any, bettingTypeId: number): boolean {
    const wager = hippodrome?.wager_details?.find((w: any) => Number(w.type) === bettingTypeId);
    if (!wager) return false;
    const runsList: any[] = Array.isArray(wager.runs) ? wager.runs : String(wager.runs ?? "").split(",");
    return runsList.some((r: any) => Number(r) === Number(run.no));
}

export function fobOddsEntry(run: any, bettingTypeId: any, oddKey: any): any {
    if (!BettingTypesMap[Number(bettingTypeId)]?.isFavoriteExcluded) {
        return run?.event_odds?.[oddKey];
    }
    if (!run?.fob_ww_status) return undefined;
    const horseNo = String(oddKey).replace('WW', '');
    return Object.values<any>(run.fob_ww ?? {}).flat().find((e: any) => String(e?.horse_no) === horseNo);
}

export function fobExcludedHorseNo(run: any): string | null {
    const entry = Object.values<any>(run?.fob_ww ?? {}).flat().find((e: any) => e?.excluded_horse != null);
    return entry ? String(entry.excluded_horse) : null;
}

export function runHasFavoriteExcludedMarket(run: any): boolean {
    return !!run?.fob_ww_status && Object.values<any>(run.fob_ww ?? {}).flat().some(isOddsEntrySelectable);
}

export function runHasH2HMarket(run: any): boolean {
    return !!(run?.fob_h2h_status && run?.fob_h2h && Object.keys(run.fob_h2h).length > 0);
}

export function hippodromeHasFobContent(hippodrome: any, bettingType: any): boolean {
    if (!bettingType) return true;
    const runs: any[] = hippodrome?.runs ?? [];
    if (bettingType.isHeadToHead) return runs.some(runHasH2HMarket);
    if (bettingType.isFavoriteExcluded) return runs.some(runHasFavoriteExcludedMarket);
    return runs.some((run: any) => isRunEligibleForFobBettingType(hippodrome, run, bettingType.id));
}

/**
 * 4nala'nın FOB sayfası LİSTE biçimli: seçili koşu yok, bağlam (kart?, tür) ikilisi.
 * `cardId` null = "Hepsi" kapsamı, o kapsam hiç kırılmaz.
 */
export function fobListSelectionBreak(params: {
    cardId: any;
    fobCards: any[];
    type: any;
}): SelectionBreak {
    const {cardId, fobCards, type} = params;
    // Hiç FOB kartı yoksa kırılma RAPORLANMAZ: taşınacak yer de yok, sayfa kendi boş
    // durumunu gösteriyor. Aksi hâlde bülten boşken şerit döngüye girerdi.
    if (fobCards.length === 0) return "ok";
    const scoped = cardId != null;
    if (scoped && !cardStillListed(cardId, fobCards)) return "card";
    if (!type) return "ok";
    const scope = scoped ? fobCards.filter((el: any) => el._id === cardId) : fobCards;
    if (!scope.some((el: any) => hippodromeHasFobContent(el, type))) return "type";
    return "ok";
}

/**
 * Kapsamda içeriği olan tür: mevcut tür oynanıyorsa KORUNUR, yoksa katalog sırasındaki
 * ilk uygun tür. Hiçbiri yoksa null.
 */
export function nextFobTypeWithContent(scope: any[], current: any): any {
    if (current && scope.some((el: any) => hippodromeHasFobContent(el, current))) {
        return current;
    }
    for (const bt of Object.values<any>(BettingTypesMap)) {
        if (!bt?.isFob || !bt?.isSelectable) continue;
        if (scope.some((el: any) => hippodromeHasFobContent(el, bt))) return bt;
    }
    return null;
}

// --- kullanıcıya gösterilecek metinler -------------------------------------------------

/**
 * Modal gövdesi `dangerouslySetInnerHTML` ile basılıyor, yani `\n` hiçbir şey yapmıyor ve
 * uzun metin tek blok hâlinde okunmuyordu. Satırlar ayrı paragraflara çevriliyor.
 */
export function asModalHtml(text: string): string {
    return text
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .map((line) => `<p class="mb-1">${line}</p>`)
        .join("");
}

/** Müşterek modalının gövdesi. Kademe adı değil, kapanan ŞEYİN adı gösterilir. */
export function mutualBreakMessage(params: {
    brk: SelectionBreak;
    cardName?: string;
    typeName?: string;
    subTypeId?: any;
    clearedCount?: number;
}): string {
    const {brk, cardName, typeName, subTypeId, clearedCount = 0} = params;
    const lines: string[] = [];
    if (brk === "run") {
        lines.push(`${subTypeId ?? "-"}. koşu için bahisler kapandı.`);
    } else if (brk === "type") {
        lines.push(`${typeName ?? "Seçili"} bahsi kapandı.`);
    } else if (brk === "card") {
        lines.push(`${cardName ?? "Seçili hipodrom"} kartındaki bahisler kapandı.`);
    } else {
        return "";
    }
    if (clearedCount > 0) {
        lines.push(`Kuponunuzdaki ${clearedCount} seçim temizlendi.`);
    }
    lines.push("Güncel bültene dönülüyor.");
    return lines.join("\n");
}

/** Sabit ihtimallide geçişin bildirimi. */
export function fobMoveNotice(params: {brk: SelectionBreak; typeName?: string}): string {
    const {brk, typeName} = params;
    if (brk === "card") {
        return "Seçili hipodromda sabit ihtimalli bahis kalmadı.\nTüm hipodromlara dönüldü.";
    }
    if (brk === "type") {
        return `${typeName ?? "Seçili bahis türü"} kapandı.\nAçık olan bahis türüne geçildi.`;
    }
    return "";
}

// --- ardışık koşu zinciri (Ganyan → 7'li) -----------------------------------------------

// Bir ayak koşulunca müşterek kuponu sıfırlayıp kullanıcıyı bültenin dışına atmak yerine,
// kuponu BOŞALTIP zincirde bir alt basamağa bırakıyoruz:
// 7'li → 6'lı → 5'li → 4'lü → 3'lü → Çifte → Ganyan.
//
// Kupon TAŞINMIYOR. Taşımak, kullanıcının o bahis için kurmadığı bir kuponu ona aitmiş gibi
// göstermek olurdu; eksik ya da hatalı taşıma kafa karıştırır.
//
// Havuz yapısı karttan karta değişiyor, bu yüzden zincir KODLANMIYOR, tarifede ARANIYOR:
//   * İzmir (7 koşu, iç içe): 7'li(1-7) → 6'lı(2-7) → ... → Çifte(6-7) → Ganyan(7)
//   * Saratoga: iki paralel zincir, 6'lı[1,5] ve 5'li[2,6] gibi çok gruplu türler
//   * Ankara: 6'lı(1-6) + 5'li(4-8) - ÖRTÜŞEN ama iç içe DEĞİL
//   * Karma: 6'lı(1-6) ve Ganyan, ara basamak hiç yok
// Hiçbir zincir türü kalmamışsa çağıran bugünkü sıfırlama modalını gösterir.

/** Zincire girebilen tür. Ölçüt katalogdaki `isLegChain` bayrağı. */
export function isChainableLegType(type: any): boolean {
    return type?.isLegChain === true;
}

/** Alt tür etiketinden ("3-4-5-6-7") ayak numaraları. */
export function groupRunNos(label: any): number[] {
    // Boş parçalar ÖNCE eleniyor: `Number("")` sıfır döndürüyor ve etiket boşken hayalî bir
    // "0. ayak" üretiyordu (Dart'ta `int.tryParse` bunu kendiliğinden eliyor).
    return String(label ?? "")
        .split("-")
        .map((p) => p.trim())
        .filter((p) => p !== "")
        .map((p) => Number(p))
        .filter((n) => Number.isFinite(n));
}

/** Zincirde bir alt basamak. */
export type DegradeTarget = {
    /** Hedef tür (katalog nesnesi + kartın `runs`u). */
    type: any;
    /** Hedef grubun başlangıç ayağı = yeni alt tür id'si. */
    subTypeId: number;
    /** Hedef grubun ayakları. */
    runs: number[];
    /** Kapanan ayak: mevcut grubun İLKİ. Metinde kullanılıyor. */
    closedRunNo: number;
    /** Sayfaların beklediği alt tür biçimi. */
    subType: {id: number; label: string};
};

/**
 * Zincirde bir alt basamak: bir alt tür (kartta yoksa bir alttaki), o türün de bulunulan
 * gruba EN ÇOK ayak paylaşan grubu; eşitlikte en erken başlayan. Yoksa null.
 */
export function degradeTarget(params: {
    brk: SelectionBreak;
    card: any;
    currentType: any;
    groupRuns: number[];
}): DegradeTarget | null {
    const {brk, card, currentType, groupRuns} = params;
    // KART kademesi kırıldıysa zincir DENENMEZ: kapalı kart `wager_details` taşımaya devam
    // edebiliyor, o yüzden burada hedef "bulunur" ve kullanıcı kapanmış kartın üstünde
    // bırakılırdı. Karma hipodromu tam olarak bu: bir koşu başlayınca kart komple kapanıyor,
    // yani kırılma run/type değil CARD seviyesinde geliyor ve doğru davranış sonraki
    // hipodroma geçmek. Bayrak parametre olarak isteniyor ki çağıran unutamasın.
    if (brk === "card") return null;
    if (!card || !isChainableLegType(currentType)) return null;
    if (groupRuns.length < 2) return null; // Ganyan zincirin sonu
    const closed = groupRuns[0];

    // Tür KART VERİSİYLE döndürülüyor (`buildMutualBettingTypes` ile aynı biçim: katalog +
    // kartın fiyatı + kartın koşuları). Ham katalog nesnesi dönseydi çağıran tarafta birim
    // fiyat ve `runs` kaybolurdu.
    const candidates = (card.wager_details ?? [])
        .map((w: any) => {
            const catalog: any = BettingTypesMap[Number(w.type)];
            if (!catalog) return null;
            const runs = wagerRunNos(w);
            return {type: {...catalog, price: w.price ?? catalog.price, runs}, runs};
        })
        .filter((c: any) =>
            c && isChainableLegType(c.type) && Number(c.type.column) < groupRuns.length)
        .sort((a: any, b: any) => Number(b.type.column) - Number(a.type.column));

    for (const candidate of candidates) {
        const step = Number(candidate.type.subType ?? 1);
        let best: DegradeTarget | null = null;
        let bestOverlap = -1;
        for (const start of candidate.runs) {
            const group = Array.from({length: step}, (_, i) => start + i);
            // Kapanan ayağı içeren grup ASLA hedef olamaz. Sunucu tarifeyi kırpıyor ama buna
            // güvenmiyoruz: kullanıcıyı koşulmuş bir ayağın üstüne bırakmak olurdu.
            if (group.includes(closed)) continue;
            const overlap = group.filter((r) => groupRuns.includes(r)).length;
            if (overlap > bestOverlap) {
                bestOverlap = overlap;
                best = {
                    type: candidate.type,
                    subTypeId: start,
                    runs: group,
                    closedRunNo: closed,
                    subType: {id: start, label: group.join("-")},
                };
            }
        }
        if (best) return best;
    }
    return null;
}

/**
 * Zincirde bir alt basamağa geçişin modal metni. Kupon TAŞINMIYOR, boşaltılıyor - metin de
 * bunu birebir söylüyor, "taşındı" gibi bir ima bırakmıyor.
 */
export function degradeMessage(params: {
    closedRunNo: number;
    fromTypeName: string;
    toTypeName: string;
    clearedCount?: number;
}): string {
    const {closedRunNo, fromTypeName, toTypeName, clearedCount = 0} = params;
    const lines = [`${closedRunNo}. koşu için bahisler kapandı, ${fromTypeName} sona erdi.`];
    lines.push(clearedCount > 0
        ? `Kuponunuz temizlendi, ${toTypeName} bahsine geçildi.`
        : `${toTypeName} bahsine geçildi.`);
    return lines.join("\n");
}

// --- kupon satırının neden oynanamadığı --------------------------------------------------

/**
 * Bir seçim İKİ ayrı sebeple oynanamaz olur ve ikisi farklı şeyler:
 *   * `run`   - koşu kapandı. Bunu zincir/sıfırlama akışı yakalıyor (kırılma tetikleniyor,
 *               kupon boşalıyor). Geriye yalnız dar bir pencere kalıyor: sunucu `run.status`ü
 *               `wager_details` kırpmasından ÖNCE güncellerse bir-iki tick boyunca koşu
 *               kapalı ama alt tür hâlâ listede olur.
 *   * `horse` - AT KOŞUDAN ÇEKİLDİ. Kart, tür ve koşu açık; seçim bağlamında kırılan hiçbir
 *               şey yok, yani guard hiç çalışmaz. Kupondaki bacak yine de geçersizdir ve
 *               "Hemen Oyna" kilitlenir - bu durumu kullanıcıya söyleyen tek yer uyarı şeridi.
 */
export type MutualStaleReason = "run" | "horse";

export function mutualStaleReason(run: any, horseNo: any): MutualStaleReason | null {
    if (!run || Number(run.status) !== 0) return "run";
    // "s" tek bir ata değil "bu ayaktaki tüm atlar"a karşılık geliyor; koşu açıksa geçerli.
    if (String(horseNo) === "s") return null;
    const horse = run.horses?.find((h: any) => String(h.no) === String(horseNo));
    return !horse || horse.run_status === false ? "horse" : null;
}

/** Uyarıda gösterilecek özet: at çekilmesinde KOŞU + AT NUMARALARI, kapanmada koşu numaraları. */
export type StaleSummary = {
    withdrawn: {run: number; horses: string[]}[];
    closedRuns: number[];
};

export function summarizeStale(
    rows: {run: any; horse: any; reason: MutualStaleReason | null}[]
): StaleSummary {
    const byRun = new Map<number, string[]>();
    const closed = new Set<number>();
    for (const row of rows) {
        const run = Number(row.run);
        if (!Number.isFinite(run)) continue;
        if (row.reason === "horse") {
            const horses = byRun.get(run) ?? [];
            if (!horses.includes(String(row.horse))) horses.push(String(row.horse));
            byRun.set(run, horses);
        } else if (row.reason === "run") {
            closed.add(run);
        }
    }
    return {
        withdrawn: Array.from(byRun.entries())
            .sort((a: [number, string[]], b: [number, string[]]) => a[0] - b[0])
            .map(([run, horses]: [number, string[]]) => ({
                run,
                horses: horses.sort((a: string, b: string) => Number(a) - Number(b)),
            })),
        closedRuns: Array.from(closed).sort((a: number, b: number) => a - b),
    };
}

/** "3. koşuda 1 numaralı at koşmaz durumda" / "4. koşu kapandı" - ikisi varsa ikisi de. */
export function staleSummaryText(summary: StaleSummary): string {
    const parts: string[] = [];
    if (summary.withdrawn.length > 0) {
        parts.push(
            summary.withdrawn
                .map((g) => `${g.run}. koşuda ${g.horses.join(", ")} numaralı at`)
                .join(", ") + " koşmaz durumda"
        );
    }
    if (summary.closedRuns.length > 0) {
        parts.push(`${summary.closedRuns.join(", ")}. koşu kapandı`);
    }
    return parts.join(" · ");
}
